if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      const isGemistMarketplace = this.constructor.isGemistMarketplace(this);
      const submittedFromPopup = Boolean(evt.submitter && evt.submitter.closest('.wwyldn'));

      if (isGemistMarketplace && !submittedFromPopup && !this.constructor.hasSelectedGemistDiamond()) {
        const popup = this.querySelector('.wwyldn') || document.querySelector('.wwyldn');
        if (popup) popup.style.setProperty('display', 'flex', 'important');
        return;
      }

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      
      // Check if free promo is enabled and get variant ID from DOM
      const freePromoSelector = document.querySelector('.free-diamond-selector[data-free-promo-enabled="true"]');
      const isPromoEnabled = freePromoSelector !== null;
      const freeVariantId = freePromoSelector?.dataset.freeVariantId || null;
      const minimumPurchase = freePromoSelector?.dataset.minimumPurchase ? parseInt(freePromoSelector.dataset.minimumPurchase, 10) : 0;
      
      // Check if free diamond checkbox is checked by verifying the hidden input exists in formData
      let freeDiamondIncluded = false;
      if (isPromoEnabled) {
        // Check if the hidden input exists in the form
        const freeDiamondInput = document.getElementById('free-diamond-selected');
        if (freeDiamondInput && freeDiamondInput.value === 'Yes') {
          // Also verify it's in the formData
          for (const [key, value] of formData) {
            if( key === 'properties[Free earring]' && value === 'Yes') {
              freeDiamondIncluded = true;
              break;
            }
          }
        }
      }

      config.body = formData

      // Helper function to check if minimum purchase is met
      const checkMinimumPurchase = async () => {
        if (minimumPurchase <= 0) return true; // No minimum required
        
        try {
          const cartResponse = await fetch(`${routes.cart_url}.js`);
          const cartData = await cartResponse.json();
          // Check subtotal (items_subtotal_price) which is before discounts
          return cartData.items_subtotal_price >= minimumPurchase;
        } catch (error) {
          console.error('Error checking minimum purchase:', error);
          return false; // Fail safe: don't add free product if we can't verify
        }
      };

      // Helper function to check if free earring already exists in cart
      const checkFreeEarringExists = async (freeVariantIdToCheck) => {
        if (!freeVariantIdToCheck) return false;
        
        try {
          // Small delay to ensure cart is updated after adding main product
          await new Promise(resolve => setTimeout(resolve, 300));
          
          const cartResponse = await fetch(`${routes.cart_url}.js`, {
            cache: 'no-cache',
            headers: {
              'Cache-Control': 'no-cache'
            }
          });
          
          if (!cartResponse.ok) {
            console.warn('[Free Earring Check] Cart fetch failed:', cartResponse.status);
            // If we can't fetch cart, be conservative and assume it exists to prevent duplicates
            return true;
          }
          
          const cartData = await cartResponse.json();
          
          if (!cartData || !cartData.items || !Array.isArray(cartData.items)) {
            console.warn('[Free Earring Check] Invalid cart data structure');
            // If cart structure is invalid, be conservative
            return true;
          }
          
          // Check if any item in cart is a free promo item
          const freeEarringExists = cartData.items.some(item => {
            // Check by variant ID (compare as numbers)
            if (item.variant_id) {
              const itemVariantId = parseInt(item.variant_id);
              const targetVariantId = parseInt(freeVariantIdToCheck);
              if (itemVariantId === targetVariantId) {
                return true;
              }
            }
            
            // Check by free promo property
            if (item.properties) {
              // Handle object format (Shopify standard)
              if (typeof item.properties === 'object' && !Array.isArray(item.properties)) {
                if (item.properties['_is_free_promo'] === 'true') {
                  return true;
                }
              }
              // Handle array format (if properties is an array)
              if (Array.isArray(item.properties)) {
                const freeProp = item.properties.find(p => 
                  p && (p.name === '_is_free_promo' || p.key === '_is_free_promo') && p.value === 'true'
                );
                if (freeProp) {
                  return true;
                }
              }
            }
            return false;
          });
          
          return freeEarringExists;
        } catch (error) {
          console.error('[Free Earring Check] Error checking if free earring exists:', error);
          // Fail safe: if we can't verify, assume it exists to prevent duplicate additions
          // This prevents the exploit even if there's a network error
          return true;
        }
      };

      // Helper function to add free earring if conditions are met
      const addFreeEarringIfNeeded = async (freeVariantIdToAdd) => {
        if (!freeVariantIdToAdd) return false;
        
        try {
          // Check if minimum purchase is met
          const meetsMinimum = await checkMinimumPurchase();
          if (!meetsMinimum) {
            return false;
          }
          
          // Check if free earring already exists
          const freeEarringExists = await checkFreeEarringExists(freeVariantIdToAdd);
          if (freeEarringExists) {
            console.log('[Free Earring] Free earring already exists in cart, skipping addition');
            return false;
          }
          
          // Add free earring
          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: parseInt(freeVariantIdToAdd),
                quantity: 1,
                properties: {
                  '_is_free_promo': 'true'
                }
              }]
            })
          });
          
          const addData = await addResponse.json();
          if (addData.error) {
            console.error('[Free Earring] Error adding free earring:', addData.error);
            return false;
          }
          
          console.log('[Free Earring] Free earring added successfully');
          return true;
        } catch (error) {
          console.error('[Free Earring] Error in addFreeEarringIfNeeded:', error);
          return false;
        }
      };

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then(async (response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (isGemistMarketplace) {
            if (freeDiamondIncluded && isPromoEnabled && freeVariantId) {
              await addFreeEarringIfNeeded(freeVariantId);
            }
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          } else if (!this.cart) {
            // Add free product if promo is enabled, checkbox is checked, and conditions are met
            if(freeDiamondIncluded && isPromoEnabled && freeVariantId) {
              await addFreeEarringIfNeeded(freeVariantId);
            }

            window.location = window.routes.cart_url;
            return;
          }

          this.error = false;
          
          // Add free product if promo is enabled, checkbox is checked, and conditions are met (for cart drawer/notification)
          if (freeDiamondIncluded && isPromoEnabled && freeVariantId) {
            const freeEarringAdded = await addFreeEarringIfNeeded(freeVariantId);
            
            // Refresh cart contents to show the free item if it was added
            if (freeEarringAdded && this.cart) {
              const sections = this.cart.getSectionsToRender().map((section) => section.id);
              const sectionsResponse = await fetch(`${routes.cart_url}?sections=${sections.join(',')}`);
              const sectionsData = await sectionsResponse.json();
              const updatedResponse = {
                ...response,
                sections: sectionsData.sections || response.sections
              };
              this.cart.renderContents(updatedResponse);
            } else {
              // Render cart normally (free earring already exists or wasn't added)
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener('modalClosed', () => {
                  setTimeout(() => { this.cart.renderContents(response) });
                }, { once: true });
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(response);
              }
            }
          } else {
            // Normal cart rendering without free product
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener('modalClosed', () => {
                setTimeout(() => { this.cart.renderContents(response) });
              }, { once: true });
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    static isGemistMarketplace(formEl) {
      if (formEl && formEl.hasAttribute('data-gemist-marketplace')) return true;
      const root = document.getElementById('app-root');
      if (!root) return false;
      const tags = (root.getAttribute('data-product-tags') || '').toLowerCase();
      return tags.includes('gemist-stone-marketplace');
    }

    static hasSelectedGemistDiamond() {
      if (typeof window.__kirkGemistDiamondSelected === 'function') {
        return window.__kirkGemistDiamondSelected();
      }

      const selection = window.gemistStoneMarketplace && window.gemistStoneMarketplace.selection;
      return Boolean(selection && (selection.selected || selection.diamond));
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
